import { AlertCircle, Check, CheckCircle2, ClipboardCopy, RefreshCw, Search, TriangleAlert } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { TRACKING_DIAGNOSTICS_CHANGED_EVENT } from '../components/navigation/TrackingDiagnosticsAlert'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { EmptyState } from '../components/ui/empty-state'
import {
  getTrackingDiagnosticIncidents,
  updateTrackingDiagnosticStatus,
  type TrackingDiagnosticIncident,
  type TrackingDiagnosticStatus
} from '../lib/api/trackingDiagnostics'
import { cn } from '../utils/cn'

const statusOptions: Array<{ value: TrackingDiagnosticStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' }
]

const indicatorLabels: Record<string, string> = {
  location_gap: 'Location gap',
  incomplete_session: 'Incomplete session',
  task_manager_error: 'Background task error',
  persistence_failure: 'On-device persistence failure',
  unexpected_save_navigation: 'Unexpected save screen'
}

const formatDateTime = (value: string | number) => {
  const date = typeof value === 'number' ? new Date(value) : new Date(value)
  if (Number.isNaN(date.getTime())) {
    return String(value)
  }
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'medium'
  }).format(date)
}

const buildClipboardText = (incident: TrackingDiagnosticIncident) => JSON.stringify({
  stats: {
    incidentId: incident.id,
    clientIncidentId: incident.clientIncidentId,
    indicator: incident.indicator,
    status: incident.status,
    userId: incident.userId,
    sessionId: incident.sessionId,
    detectedAt: incident.detectedAt,
    enqueuedAt: incident.enqueuedAt,
    receivedAt: incident.receivedAt,
    updatedAt: incident.updatedAt,
    acknowledgedAt: incident.acknowledgedAt,
    acknowledgedByUserId: incident.acknowledgedByUserId,
    appVersion: incident.appVersion,
    buildNumber: incident.buildNumber,
    platform: incident.platform,
    appVariant: incident.appVariant,
    context: incident.context,
    lifecycleEventCount: incident.lifecycleEvents.length
  },
  lifecycleEvents: incident.lifecycleEvents
}, null, 2)

export const TrackingDiagnosticsPage = () => {
  const [status, setStatus] = useState<TrackingDiagnosticStatus>('open')
  const [search, setSearch] = useState('')
  const [items, setItems] = useState<TrackingDiagnosticIncident[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadIncidents = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }
    setError(null)
    try {
      const result = await getTrackingDiagnosticIncidents({
        status,
        search: search.trim() || undefined,
        limit: 100
      })
      setItems(result.items)
    } catch (loadError) {
      console.error('Failed to load tracking diagnostic incidents', loadError)
      setError('Could not load mobile tracking alerts right now.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [search, status])

  useEffect(() => {
    void loadIncidents()
  }, [loadIncidents])

  const changeStatus = async (
    incident: TrackingDiagnosticIncident,
    nextStatus: TrackingDiagnosticStatus
  ) => {
    setUpdatingId(incident.id)
    setError(null)
    try {
      const updated = await updateTrackingDiagnosticStatus(incident.id, nextStatus)
      setItems((current) => current.filter((item) => item.id !== updated.id))
      window.dispatchEvent(new Event(TRACKING_DIAGNOSTICS_CHANGED_EVENT))
    } catch (updateError) {
      console.error('Failed to update tracking diagnostic incident', updateError)
      setError('Could not update that tracking alert.')
    } finally {
      setUpdatingId(null)
    }
  }

  const copyIncident = async (incident: TrackingDiagnosticIncident) => {
    setError(null)
    try {
      await navigator.clipboard.writeText(buildClipboardText(incident))
      setCopiedId(incident.id)
    } catch (copyError) {
      console.error('Failed to copy tracking diagnostic incident', copyError)
      setError('Could not copy that tracking alert to the clipboard.')
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">System &amp; Billing</p>
          <h1 className="text-3xl font-semibold text-foreground">Mobile tracking alerts</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Investigate automatic device reports for location gaps, interrupted sessions, background task errors,
            persistence failures, and unexpected save-screen navigation.
          </p>
        </div>

        <Button variant="outline" onClick={() => void loadIncidents(true)} disabled={refreshing}>
          <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
          Refresh
        </Button>
      </header>

      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1" aria-label="Tracking alert status">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setStatus(option.value)}
              className={cn(
                'rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
                status === option.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        <label className="flex min-w-0 items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 sm:w-80">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="sr-only">Search tracking alerts</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="User, session, or indicator"
            className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </label>
      </div>

      {error && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="flex items-start gap-3 py-4 text-rose-900">
            <AlertCircle className="mt-0.5 h-5 w-5" />
            <p className="text-sm font-semibold">{error}</p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Card><CardContent className="py-8 text-sm text-muted-foreground">Loading tracking alerts…</CardContent></Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={CheckCircle2}
              title={`No ${status} tracking alerts`}
              description="There are no device-reported tracking incidents matching these filters."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((incident) => (
            <TrackingDiagnosticCard
              key={incident.id}
              incident={incident}
              updating={updatingId === incident.id}
              copied={copiedId === incident.id}
              onChangeStatus={(nextStatus) => void changeStatus(incident, nextStatus)}
              onCopy={() => void copyIncident(incident)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const TrackingDiagnosticCard = ({
  incident,
  updating,
  copied,
  onChangeStatus,
  onCopy
}: {
  incident: TrackingDiagnosticIncident
  updating: boolean
  copied: boolean
  onChangeStatus: (status: TrackingDiagnosticStatus) => void
  onCopy: () => void
}) => (
  <article className="rounded-xl border border-amber-200 bg-card p-4 shadow-sm dark:border-amber-500/30">
    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-900">
            <TriangleAlert className="h-3.5 w-3.5" />
            {indicatorLabels[incident.indicator] ?? incident.indicator}
          </span>
          <span className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground">
            {incident.platform} {incident.appVersion}{incident.buildNumber ? ` (${incident.buildNumber})` : ''}
          </span>
          <span className="text-xs text-muted-foreground">Detected {formatDateTime(incident.detectedAt)}</span>
        </div>

        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 xl:grid-cols-4">
          <Detail label="User">
            <Link className="font-mono text-primary hover:underline" to={`/users/mobile/${encodeURIComponent(incident.userId)}`}>
              {incident.userId}
            </Link>
          </Detail>
          <Detail label="Session"><span className="font-mono text-xs">{incident.sessionId}</span></Detail>
          <Detail label="Received">{formatDateTime(incident.receivedAt)}</Detail>
          <Detail label="Variant">{incident.appVariant}</Detail>
        </dl>

        {Object.keys(incident.context).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(incident.context).map(([key, value]) => (
              <span key={key} className="rounded-md bg-muted px-2 py-1 font-mono text-xs text-muted-foreground">
                {key}={value}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap gap-2">
        {incident.status === 'open' && (
          <Button disabled={updating} onClick={() => onChangeStatus('acknowledged')}>Acknowledge</Button>
        )}
        {incident.status === 'acknowledged' && (
          <Button disabled={updating} onClick={() => onChangeStatus('resolved')}>Resolve</Button>
        )}
        {incident.status !== 'open' && (
          <Button variant="outline" disabled={updating} onClick={() => onChangeStatus('open')}>Reopen</Button>
        )}
        <Button variant="outline" onClick={onCopy}>
          {copied ? <Check className="h-4 w-4" /> : <ClipboardCopy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy to clipboard'}
        </Button>
      </div>
    </div>

    <details className="mt-4 rounded-lg border border-border bg-muted/20 px-3 py-2">
      <summary className="cursor-pointer text-sm font-semibold text-foreground">
        Lifecycle trace ({incident.lifecycleEvents.length} events)
      </summary>
      <ol className="mt-3 max-h-96 space-y-2 overflow-y-auto border-l border-border pl-4">
        {incident.lifecycleEvents.map((event, index) => (
          <li key={`${event.sequence}-${event.timestamp}-${index}`} className="text-xs">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-semibold text-foreground">{event.name}</span>
              <span className="text-muted-foreground">{formatDateTime(event.timestamp)}</span>
              {event.appState && <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">{event.appState}</span>}
            </div>
            {Object.keys(event.details).length > 0 && (
              <p className="mt-1 break-words font-mono text-muted-foreground">
                {Object.entries(event.details).map(([key, value]) => `${key}=${value}`).join(' · ')}
              </p>
            )}
          </li>
        ))}
      </ol>
    </details>
  </article>
)

const Detail = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="mt-0.5 break-all text-foreground">{children}</dd>
  </div>
)
